<script>
import axios from "axios";
import Swal from 'sweetalert2';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({
  breaks: true,
  linkify: true,
});

export default {
  name: "Cart",

  data() {
    return {
      cart: null,
      md: md,
      codeToApply: null
    };
  },

  async created() {
    const resp = await axios.get('/Cart');
    this.cart = resp.data;
  },

  methods: {
    addProduct(sku, id) {
      Swal.fire({
        icon: 'info',
        title: 'Updating Your Cart',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      axios.get(`/Cart/add?sku=${sku}&suggestion=${id}`).then((response) => {
        this.cart = response.data;
        Swal.close();
      })
    },

    async applyCode() {
      Swal.fire({
        icon: 'info',
        title: 'Applying code to your cart',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });

      try {
        const resp = await axios.post(`/api/cart/${this.cart.cartId}/org-code`, {
          orgCode: this.codeToApply
        });
        this.cart = resp.data;
        this.codeToApply = null;
        Swal.close();
      }catch(e) {
        if (e.response && e.response.status === 422) {
          Swal.close();
          Swal.fire({
            icon: 'error',
            title: 'Unable to apply code to your cart',
            text: e.response.data.errors[0],
            allowOutsideClick: true,
            allowEscapeKey: true,
            allowEnterKey: true,
            showConfirmButton: true,
          });
        }
      }
    },

    hasSku(sku) {
      return this.cart?.items.filter((item) => item.sku === sku).length > 0;
    },

    removeCode() {
      Swal.fire({
        icon: 'info',
        title: 'Updating Your Cart',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      axios.delete(`/api/cart/${this.cart.cartId}/org-code`).then((response) => {
        this.cart = response.data;
        Swal.close();
      })
    },

    removeProduct(sku) {
      Swal.fire({
        icon: 'info',
        title: 'Updating Your Cart',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      axios.get(`/Cart/remove?sku=${sku}`).then((response) => {
        this.cart = response.data;
        Swal.close();
      })
    }
  },
}
</script>

<template>
  <div v-if="cart !== null" class="container py-8 grid grid-cols-12 gap-4">
    <div v-if="cart.items.length === 0" class="col-span-12 lg:col-span-9 border border-4 border-hurst-teal rounded-lg p-6">
      <h2 class="text-hurst-teal text-xl font-semibold">Your Cart is Empty</h2>
      <a href="/choose-your-review"
         class="w-full md:w-auto my-6 text-white font-semibold text-base py-2 md:py-4 px-4 md:px-16 rounded-[80px] bg-gradient-to-br from-[#ffa807] to-[#EE5914] hover:from-[#EE5914] hover:to-[#ffa807] transition-all duration-500 ease-in-out bg-size-200 bg-pos-0 hover:bg-pos-100 text-center block lg:text-left lg:inline-block self-center">
        Find A Review
      </a>
    </div>
    <template v-else>
      <div class="col-span-12 lg:col-span-9">
        <ul class="space-y-6">
          <li v-for="item in cart.items"
              class="bg-white border border-4 border-hurst-teal rounded-lg p-6 flex flex-col lg:flex-row lg:justify-between
              lg:items-start shadow-light-2
            ">
            <div class="flex-1">
              <h2 class="text-hurst-teal text-xl font-semibold">{{ item.name }}</h2>
              <div class="prose prose-sm max-w-none text-hurst-blue prose-h3:font-bold prose-h3:text-hurst-blue prose-h3:text-lg
            prose-ul:columns-xs prose-li:mt-0 prose-li:mb-0 prose-li:font-medium
          ">
                <div v-if="item.details !== null" v-html="md.render(item.details)"></div>
                <div v-if="item.description !== null" v-html="md.render(item.description)"></div>
              </div>
            </div>
            <div class="flex flex-row-reverse lg:flex-row justify-between lg:justify-start space-x-4 items-center">
              <p class="text-hurst-blue font-semibold text-xl flex space-x-1 items-center">
                <span v-if="item.originalPrice !== item.discountedPrice" class="text-sm text-right text-hurst-dark-gray line-through">${{item.originalPrice}}</span>
                <span>${{ item.subTotalDue ?? item.discountedPrice }}</span>
              </p>
              <div>
                <button :title="`Remove ${item.name} from cart`"
                        @click.prevent="removeProduct(item.sku)"
                        class="text-black  shadow-light-2 rounded-full w-9 h-9 flex justify-center items-center">
                  <i class="w-5 h-5 fa-light fa-trash-can"></i>
                </button>
              </div>
            </div>
          </li>
        </ul>
        <div v-if="cart.suggestedProducts?.length > 0" class="mt-12 border-t border-hurst-dark-teal pt-6">
          <p class="text-center">
          <span
              class="block text-hurst-dark-teal text-sm md:text-xl font-medium">Build Your Confidence with these</span>
            <span class="block text-hurst-light-blue text-xl md:text-4xl font-bold">Hurst Review Extras</span>
          </p>
          <ul class="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <li v-for="item in cart.suggestedProducts"
                class="bg-white flex flex-col border border-4 border-hurst-teal rounded-lg p-6 shadow-light-2">
              <h2 class="text-xl text-center font-medium text-hurst-teal">{{ item.name }}</h2>
              <div class="flex-grow prose prose-sm max-w-none text-hurst-blue prose-h3:font-bold prose-h3:text-hurst-blue
              prose-h3:text-xl prose-h3:text-center prose-h2:text-hurst-dark-gray prose-h2:text-base prose-h2:font-medium
              prose-h2:text-center prose-h4:text-hurst-blue prose-h4:font-medium prose-h4:text-base prose-h4:text-center
              prose-strong:text-hurst-dark-orange prose-li:font-medium prose-ul:mt-6 prose-h2:mt-1">
                <div v-if="item.description !== null" v-html="md.render(item.description)"></div>
                <div v-if="item.productDescription !== null" v-html="md.render(item.productDescription)"></div>
              </div>
              <div class="flex justify-center">
                <button v-if="!hasSku(item.sku)" type="button"
                        @click.prevent="addProduct(item.sku, item.id)"
                        class="w-full md:w-auto my-6 text-white font-semibold text-base py-2 md:py-4 px-4 md:px-16 rounded-[80px] bg-gradient-to-br from-[#ffa807] to-[#EE5914] hover:from-[#EE5914] hover:to-[#ffa807] transition-all duration-500 ease-in-out bg-size-200 bg-pos-0 hover:bg-pos-100 text-center block lg:text-left lg:inline-block self-center">
                  Add to Cart
                </button>
                <div v-else
                     @click.prevent="addProduct(item.sku)"
                     class="w-full md:w-auto my-6 text-white font-semibold text-base py-2 md:py-4 px-4 md:px-16 rounded-[80px] bg-hurst-off-white text-center block lg:text-left lg:inline-block self-center">
                  Added to Cart
                </div>
              </div>
              <div
                  data-pp-message
                  data-pp-style-layout="text"
                  data-pp-style-logo-type="inline"
                  data-pp-style-text-color="black"
                  :data-pp-amount="item.price">
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div class="col-span-12 order-first lg:order-none lg:col-span-3 space-y-4">
        <div class="bg-white rounded p-5 shadow-light-2">
          <h3 class="font-semibold text-xl text-hurst-light-blue">School/Promo Code</h3>
          <form @submit.prevent="applyCode" v-if="cart.orgCode === null">
            <label class="sr-only" for="orgCode">Enter your School/Promo code</label>
            <input type="text" id="orgCode"
                   v-model="codeToApply"
                   class="mt-4 block w-full px-4 py-2.5 rounded border border-hurst-blue placeholder:text-hurst-light-blue placeholder:opacity-35"
                   required placeholder="promocode123">
            <button type="submit" class="font-bold text-hurst-blue text-sm py-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 mr-0.5">
                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
              </svg>
              Apply Code
            </button>
          </form>
          <div v-else class="py-4">
            <div class="p-4 bg-[#6CFFD5] text-xs text-[#00352F] font-semibold">
              Your discount code {{ cart.registrationCode ?? cart.orgCode.orgCode }} has been applied
            </div>
            <button type="button"
                    @click.prevent="removeCode"
                    class="font-bold text-hurst-blue text-sm py-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 mr-0.5">
                <path fill-rule="evenodd" d="M4 10a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 10Z" clip-rule="evenodd" />
              </svg>
              Remove
            </button>
          </div>
        </div>
        <div class="bg-white rounded p-5 shadow-light-2">
          <header class="flex justify-between items-center border-b border-neutral-300 pb-4">
            <h3 class="font-semibold text-xl text-hurst-light-blue flex justify-between">Summary</h3>
            <p class="text-sm text-hurst-blue font-medium">{{cart.items.length}} items</p>
          </header>
          <div class="pt-4 pb-10">
            <div class="flex justify-between items-center">
              <h3 class="font-semibold text-xl text-hurst-blue flex justify-between">Subtotal</h3>
              <p class="font-semibold text-xl text-hurst-blue">${{cart.amountOwedByStudent}}</p>
            </div>
            <p class="mt-2 font-medium text-hurst-blue text-xs-plus">Sales tax &amp; Shipping calculated in checkout.</p>
            <div class="mt-4 bg-hurst-light-gray p-6">
              <div
                  data-pp-message
                  data-pp-style-layout="text"
                  data-pp-style-logo-type="inline"
                  data-pp-style-text-color="black"
                  data-pp-style-text-size="16"
                  :data-pp-amount="cart.amountOwedByStudent">
              </div>
            </div>
            <div>
              <a href="/Checkout"
                 class="w-full my-6 text-white font-semibold text-base py-2 md:py-4 px-4 md:px-16 rounded-[80px] bg-gradient-to-br from-[#ffa807] to-[#EE5914] hover:from-[#EE5914] hover:to-[#ffa807] transition-all duration-500 ease-in-out bg-size-200 bg-pos-0 hover:bg-pos-100 text-center block self-center">
                Checkout
              </a>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<style scoped>

</style>